const striptags = require('striptags');
import rangy from 'rangy';
import 'rangy/lib/rangy-textrange';
import { constants } from './constants';

const TEXT_NODE = 3;

export var dom = {
    copyText,
    deselect,
    focusableElements,
    setBestFontSize,
    getCurrentRange,
    getText,
    getTextLength,
    insertCaretAt,
    isElementVisible,
    isSingleLine,
    isTyping,
    scrollIntoView,
    select,
    selectChars,
    stripAttributes,
    stripTags,
    stripInput,
    convertHtmlToText,
    convertLegacyFormData,
    getElementIndex,
    formPost,
}

function copyText(text) {
    let elem = createFakeTextArea();
    document.addEventListener('click', () => { });
    document.body.appendChild(elem);
    elem.value = text;
    elem.select();
    document.execCommand('copy');
    document.removeEventListener('click', () => { });
    document.body.removeChild(elem);

    function createFakeTextArea() {
        let fakeElem = document.createElement('textarea');

        // Prevent zooming on iOS
        fakeElem.style.fontSize = '12pt';
        // Reset box model
        fakeElem.style.border = '0';
        fakeElem.style.padding = '0';
        fakeElem.style.margin = '0';
        // Move element out of screen horizontally
        fakeElem.style.position = 'absolute';
        fakeElem.style.left = '-9999px';
        // Move element to the same position vertically
        let yPosition = window.pageYOffset || document.documentElement.scrollTop;
        fakeElem.style.top = `${yPosition}px`;

        fakeElem.setAttribute('readonly', '');
        return fakeElem;
    }
}

function scrollIntoView(selectorOrElem, position = 'nearest') {
    // Position can be start, center, end, or nearest
    let elem = typeof selectorOrElem === 'string' ? document.querySelector(selectorOrElem) : selectorOrElem;
    elem.scrollIntoView({ behavior: 'smooth', block: position });
}

function isElementVisible(elem) {
    return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
}

function setBestFontSize({
    elem,
    maxSize = null,
    minSize = 1
}) {
    if (maxSize) maxSize = Math.round(maxSize * 100) / 100;
    let initialSize = parseFloat(elem.style.fontSize);
    if (!initialSize || (maxSize && initialSize > maxSize)) {
        initialSize = maxSize || constants.FORM_DEFAULT_FONT_SIZE;
        elem.style.fontSize = initialSize + 'pt';
    }
    let usePlaceholder = elem.placeholder && !elem.value && elem.placeholder.trim();
    if (usePlaceholder) elem.value = elem.placeholder;
    let hasOverflow = _hasOverflow(elem);
    let size;
    if (hasOverflow) {
        size = _decreaseUntilFits(elem, initialSize, minSize);
    } else {
        size = _increaseUntilFits(elem, initialSize, maxSize);
    }
    if (usePlaceholder) elem.value = '';
    return size;
}

const INCREMENT = 0.25;
function _increaseUntilFits(elem, from = 1, maxSize = null) {
    if (maxSize && from >= maxSize) return maxSize;
    from += INCREMENT;
    if (maxSize && from > maxSize) from = maxSize;
    elem.style.fontSize = from + 'pt';
    if (_hasOverflow(elem)) {
        from -= INCREMENT;
        elem.style.fontSize = from + 'pt';
        return from;
    }
    return _increaseUntilFits(elem, from, maxSize);
}

function _decreaseUntilFits(elem, from = 20, minSize = 1) {
    if (from <= minSize) return minSize;
    from -= INCREMENT;
    if (from < minSize) from = minSize;
    elem.style.fontSize = from + 'pt';
    if (_hasOverflow(elem)) {
        return _decreaseUntilFits(elem, from, minSize);
    }
    return from;
}

// copy of jQuery.text()
function getText(elem) {
    // Retrieve the text value of an array of DOM nodes
    var node,
        ret = "",
        i = 0,
        nodeType = elem.nodeType;

    if (!nodeType) {

        // If no nodeType, this is expected to be an array
        while ((node = elem[i++])) {

            // Do not traverse comment nodes
            ret += getText(node);
        }
    } else if (nodeType === 1 || nodeType === 9 || nodeType === 11) {

        // Use textContent for elements
        // innerText usage removed for consistency of new lines (jQuery #11153)
        if (typeof elem.textContent === "string") {
            return elem.textContent;
        } else {

            // Traverse its children
            for (elem = elem.firstChild; elem; elem = elem.nextSibling) {
                ret += getText(elem);
            }
        }
    } else if (nodeType === 3 || nodeType === 4) {
        return elem.nodeValue;
    }

    // Do not include comment or processing instruction nodes

    return ret;
}

function getTextLength(maybeHtmlText) {
    let elem = _getTempElem();
    elem.innerHTML = maybeHtmlText;
    let len = getText(elem).length;
    _removeTempElem(elem);
    return len;
}

function isSingleLine(elem) {
    let node = elem.childNodes[0];
    let range = document.createRange();
    let textLen = elem.innerText.length;

    range.setStart(node, 0);
    range.setEnd(node, 1);
    let rectStart = range.getBoundingClientRect();

    range.setStart(node, textLen);
    range.setEnd(node, textLen - 1);
    let rectEnd = range.getBoundingClientRect();

    return rectStart.top === rectEnd.top;
}

function isTyping() {
    let elem = document.activeElement;
    return elem.tagName === 'INPUT' || elem.getAttribute('contenteditable') === 'true';
}

// select all text in an elem
function select(elem) {
    if (elem.type === 'input' || elem.type === 'textarea') {
        return elem.select();
    }

    var range = document.createRange();
    range.selectNodeContents(elem);
    var sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
}

function insertCaretAt(elem, clientX, clientY) {
    try {
        elem.focus();
        let elemRect = elem.getBoundingClientRect();
        let x = clientX - elemRect.x;
        let y = clientY - elemRect.y;
        let sel = rangy.getSelection();
        let range = sel.getRangeAt(0);
        let distances = [];

        // find closest insert location
        for (let i = 0; i < elem.innerText.length; i++) {
            distances.push(_getIndexDistance(i));
            if (i === elem.innerText.length - 1) {
                distances.push(_getIndexDistance(i, true));
            }
        }

        let minIndex = distances.reduce((minIndex, distance, index) => {
            return distance < distances[minIndex] ? index : minIndex
        }, 0);

        selectChars(elem, minIndex, minIndex, sel, range);

        // get absolute distance between the beginning (or optionally, the end) of a character 
        // and the passed-in X & Y coordinates
        function _getIndexDistance(charIndex, afterChar = false) {
            selectChars(elem, charIndex, charIndex + 1, sel, range);
            let rangeRect = range.nativeRange.getBoundingClientRect();
            let relativeY = (rangeRect.y + rangeRect.height / 2) - elemRect.y;
            let relativeX;

            if (afterChar) {
                relativeX = rangeRect.x + rangeRect.width - elemRect.x;
            } else {
                relativeX = rangeRect.x - elemRect.x;
            }

            return Math.abs(relativeX - x) + Math.abs(relativeY - y);
        }
    } catch (err) {
        console.warn('Failed to insert caret at coordinates: ' + err.message);
    }
}

function getCurrentRange() {
    let sel = rangy.getSelection();
    return sel.getRangeAt(0);
}

function selectChars(elem, from, to, sel, range) {
    if (!sel || !range) {
        sel = rangy.getSelection();
        range = sel.getRangeAt(0);
    }

    range.selectCharacters(elem, from, to);
    sel.setSingleRange(range);
}

// remove all text selections
function deselect() {
    window.getSelection().removeAllRanges()
}

function stripAttributes(html) {
    if (!html) return '';
    let elem = _getTempElem();
    elem.innerHTML = html;
    _walkTheDOM(elem.firstChild, node => {
        if (node.nodeType === TEXT_NODE) return;

        [...node.attributes].forEach(attr => {
            node.removeAttribute(attr.name);
        });
    });
    html = elem.innerHTML;
    _removeTempElem(elem);
    return html;
}

function stripTags(html, exceptions = []) {
    return striptags(html, exceptions);
}

function focusableElements(parentNode = document, includeProgrammatic = false) {
    let sel = constants.SELECTOR_FOCUSABLE;
    if (includeProgrammatic) sel += ', [tabindex="-1"]';
    let elems = parentNode.querySelectorAll(sel);
    return Array.from(elems).filter(el => {
        return !el.hasAttribute('disabled') &&
            !el.classList.contains('disabled') &&
            (el.offsetWidth !== 0 || el.offsetHeight !== 0);
    });
}

function stripInput(el, regexp) {
    if (typeof regexp === 'string') {
        regexp = new RegExp(regexp, 'g');
    }
    let start = el.selectionStart;
    let end = el.selectionEnd;
    let diff = el.value.length;
    el.value = el.value.replace(regexp, '');
    diff -= el.value.length;
    el.selectionStart = Math.max(start - diff, 0);
    el.selectionEnd = Math.max(end - diff, 0);
    return el.value;
}

function convertHtmlToText(html) {
    html = html.replace(/<br(\s+[^>]*)?\/?>/ig, '\n');
    let el = document.createElement('div');
    el.innerHTML = html;
    return el.innerText;
}

function convertLegacyFormData({ pkg, formData, forms, createdAt }) {
    if (pkg) createdAt = pkg.revision.created_at;
    if (createdAt >= '2021-02-04T17') return;

    // Convert legacy textbox HTML data into plain text
    if (pkg) {
        forms = pkg.forms;
        formData = {};
        for (let id in pkg.form_instances) {
            formData[id] = pkg.form_instances[id].form_data;
        }
    }
    let features = {};
    for (let form of forms) {
        for (let page of form.data.pages) {
            for (let feature of page.features) {
                features[feature.id] = feature;
            }
        }
    }
    for (let formId in formData) {
        if (!formData[formId]) continue;
        for (let fieldId in formData[formId]) {
            if (!features[fieldId]) continue;
            let data = formData[formId][fieldId];
            if (typeof data.value === 'string' && features[fieldId].type === 'textbox') {
                data.value = convertHtmlToText(data.value).replace(/\n+$/, '');
            }
        }
    }
}

function getElementIndex(el) {
    return [].indexOf.call(el.parentNode.children, el);
}

function formPost(url, data = {}) {
    let form = document.createElement('form');
    form.style = 'display: none';
    form.action = url;
    form.method = 'POST';
    for (let key in data) {
        let input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = data[key];
        form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
}

// return or create an element with copied styles/text
function _getTempElem() {
    let elem = document.getElementById('temp-elem');

    if (!elem) {
        elem = document.createElement('div');
        elem.id = 'temp-elem';
        let main = document.getElementById('main');
        main.appendChild(elem);
    }

    return elem;
}

function _removeTempElem(elem) {
    elem.parentNode.removeChild(elem);
}

function _copyStyles(from, to) {
    const styles = window.getComputedStyle(from);

    if (styles.cssText !== '') {
        to.style.cssText = styles.cssText;
    } else {
        to.style.cssText = Object.values(styles).reduce(
            (css, propertyName) => 
                `${css}${propertyName}:${styles.getPropertyValue(propertyName)};`
            , '');
    }

    // copy over any em/rem values that are in the style tag so that 
    // they override the pixel values in the computed styles
    for (let prop of from.style) {
        if (from.style[prop] && from.style[prop].substr(-2) === 'em') to.style[prop] = from.style[prop];
    }

    to.style.position = 'fixed';
    to.style.top = '0';
    to.style.left = '-9999px';
    to.style.overflow = 'visible';
    to.style.lineHeight = 'initial';

    // testing block
    // {
    //     to.style.left = '50%';
    //     to.style.transform = 'translateX(-50%)';
    //     to.style.top = '30px';
    //     to.style.zIndex = 10000000;
    //     to.style.position = 'fixed';
    //     to.style.background = '#eee';
    // }

    _copyText(from, to);
}

function _copyText(from, to) {
    let text = from.innerHTML || from.value;

    if (!text) {
        text = from.getAttribute('placeholder') || '';
    }

    if (!text.trim()) {
        let placeholderChar = 'W';
        text = placeholderChar;
    }

    to.innerHTML = text;
}

function _hasOverflow(elem) {
    return elem.scrollHeight > elem.clientHeight || elem.scrollWidth > elem.clientWidth;
}

function _walkTheDOM(node, func) {
    func(node);
    node = node.firstChild;
    while (node) {
        _walkTheDOM(node, func);
        node = node.nextSibling;
    }
}